import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CataloguesService } from 'core/services/catalogues.service';
import { CoursesService } from 'core/services/courses.service';
import { GamesService } from 'core/services/games.service';

@Injectable({
  providedIn: 'root'
})
export class CourseConfigurationService {
  constructor(
    private cataloguesService: CataloguesService,
    private coursesService: CoursesService,
    private gamesService: GamesService,
    private translateService: TranslateService
  ) {}

  /**
   * Updates game, course and/or catalog item with new configuration.
   *
   * @param data   New configuration data (plus other needed fields).
   * @param course The course which configuration is being updated.
   */
  public async updateCourseConfiguration(data: any, course: any) {
    const { clientId, isClientConfig, catalogId, submitAction } = data;
    const isUserCourseOwner = clientId === course.clientId;
    const newConfigData = {};

    if (isClientConfig) {
      // We are in client's detail view and we are updating the client's
      // course configuration stored in a catalog item
      const newCatalogData = await this.updateCatalogItem(data, course.id, catalogId);
      Object.assign(newConfigData, newCatalogData);

      // If the client owns the course then course data may also be updated
      if (isUserCourseOwner) {
        const newCourseData = await this.updateCourse(data, course);
        Object.assign(newConfigData, newCourseData);
      }
    } else {
      if (['admin', 'global'].includes(submitAction) || isUserCourseOwner) {
        const newCourseData = await this.updateCourse(data, course);
        Object.assign(newConfigData, newCourseData);
      }
    }

    return newConfigData;
  }

  private async updateCourse(data: any, course: any): Promise<any> {
    const currentLang = this.translateService.currentLang;
    const courseId = course.id;
    const {
      accessChangeConfirmed,
      shouldSendDiploma,
      demoEnabled,
      publish,
      preSurveyUrl,
      postSurveyUrl,
      bonusPossible,
      credits
    } = data;

    const courseData = {
      configuration: {
        shouldSendDiploma,
        demoEnabled
      },
      publish,
      preSurveyUrl,
      postSurveyUrl,
      bonusPossible,
      credits
    };

    const updatedCourse = await this.coursesService.updateCourse(
      courseId,
      courseData,
      accessChangeConfirmed,
      currentLang
    );

    if (credits !== course.credits) {
      await this.updateGame(course.gameId, { credits });
    }

    return {
      ...updatedCourse,
      credits
    };
  }

  private async updateGame(gameId: string, data: any): Promise<any> {
    const { credits } = data;
    const gameData = {
      id: gameId,
      credits
    };

    return this.gamesService.update(gameData);
  }

  private async updateCatalogItem(data: any, courseId: string, catalogId: string) {
    const currentLang = this.translateService.currentLang;
    const { accessMode, shouldSendDiploma, accessChangeConfirmed } = data;

    const catalogItemData = {
      courseId,
      accessMode,
      configuration: {
        shouldSendDiploma
      }
    };

    await this.cataloguesService.updateCatalogItem(catalogId, catalogItemData, accessChangeConfirmed, currentLang);

    return catalogItemData;
  }
}
